import React from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeftCircle,
  CheckCircle2,
  CircleDashed,
  FolderKanban,
  PauseCircle,
  PlayCircle,
  Search,
  Sparkles,
  XCircle,
} from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import DataTable from '@/components/shared/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { accountingApi, type LookupProject } from '@/lib/api/accounting';
import { formatRial } from '@/lib/formatMoney';
import { normalizeFaSearchText } from '@/lib/textNormalize';

type ProjectRow = {
  id: number;
  label: string;
  status: string;
  progress: number;
  contractAmount: number;
};

const STATUS_SORT_RANK: Record<string, number> = {
  in_progress: 0,
  planning: 1,
  paused: 2,
  completed: 3,
  cancelled: 4,
};

function statusSortRank(status: string): number {
  return STATUS_SORT_RANK[status] ?? 50;
}

function contractAmountOf(p: LookupProject): number {
  return Number(p.final_amount ?? p.initial_estimate ?? 0) || 0;
}

function sumContractAmounts(list: LookupProject[]): number {
  return list.reduce((s, p) => s + contractAmountOf(p), 0);
}

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'in_progress', label: 'در حال اجرا' },
  { value: 'planning', label: 'برنامه‌ریزی' },
  { value: 'paused', label: 'متوقف' },
  { value: 'completed', label: 'تکمیل شده' },
  { value: 'cancelled', label: 'لغو شده' },
];

const FILTER_CHIPS: { key: string; label: string }[] = [
  { key: '', label: 'همه' },
  { key: 'in_progress', label: 'در حال اجرا' },
  { key: 'planning', label: 'برنامه‌ریزی' },
  { key: 'paused', label: 'متوقف' },
  { key: 'completed', label: 'تکمیل شده' },
  { key: 'cancelled', label: 'لغو شده' },
];

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = React.useState<LookupProject[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    setError('');
    accountingApi
      .projects()
      .then((list) => {
        setProjects(list);
        if (!list.length) {
          setError('هیچ پروژه‌ای یافت نشد.');
        }
      })
      .catch((err) => {
        setProjects([]);
        setError(err instanceof Error ? err.message : 'خطا در دریافت لیست پروژه‌ها');
      })
      .finally(() => setLoading(false));
  }, []);

  const statusMeta = React.useCallback((status: string) => {
    const map: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
      planning: { label: 'برنامه ریزی', icon: <CircleDashed className="h-4 w-4" />, className: 'bg-slate-100 text-slate-700' },
      in_progress: { label: 'در حال اجرا', icon: <PlayCircle className="h-4 w-4" />, className: 'bg-sky-100 text-sky-700' },
      completed: { label: 'تکمیل شده', icon: <CheckCircle2 className="h-4 w-4" />, className: 'bg-emerald-100 text-emerald-700' },
      paused: { label: 'متوقف', icon: <PauseCircle className="h-4 w-4" />, className: 'bg-amber-100 text-amber-700' },
      cancelled: { label: 'لغو شده', icon: <XCircle className="h-4 w-4" />, className: 'bg-rose-100 text-rose-700' },
    };
    return map[status] ?? { label: 'نامشخص', icon: <CircleDashed className="h-4 w-4" />, className: 'bg-slate-100 text-slate-700' };
  }, []);

  const searchNorm = normalizeFaSearchText(search);

  const filteredSorted = React.useMemo(() => {
    const list = projects.filter((p) => {
      if (statusFilter && (p.status || '') !== statusFilter) return false;
      if (searchNorm) {
        const hay = normalizeFaSearchText(p.label || '');
        if (!hay.includes(searchNorm)) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => {
      const ra = statusSortRank(a.status || '');
      const rb = statusSortRank(b.status || '');
      if (ra !== rb) return ra - rb;
      return (a.label || '').localeCompare(b.label || '', 'fa');
    });
  }, [projects, searchNorm, statusFilter]);

  const inProgressForStrip = React.useMemo(() => {
    if (statusFilter && statusFilter !== 'in_progress') return [];
    return projects
      .filter((p) => (p.status || '') === 'in_progress')
      .filter((p) => {
        if (!searchNorm) return true;
        return normalizeFaSearchText(p.label || '').includes(searchNorm);
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'fa'));
  }, [projects, searchNorm, statusFilter]);

  const rows = React.useMemo<ProjectRow[]>(() => {
    return filteredSorted.map((p) => ({
      id: p.id,
      label: p.label,
      status: p.status || '',
      progress: Math.max(0, Math.min(100, Number(p.progress ?? 0))),
      contractAmount: contractAmountOf(p),
    }));
  }, [filteredSorted]);

  const projectsAfterSearch = React.useMemo(() => {
    return projects.filter((p) => {
      if (!searchNorm) return true;
      return normalizeFaSearchText(p.label || '').includes(searchNorm);
    });
  }, [projects, searchNorm]);

  const statsInProgress = React.useMemo(() => {
    const list = projectsAfterSearch.filter((p) => (p.status || '') === 'in_progress');
    return { count: list.length, sum: sumContractAmounts(list) };
  }, [projectsAfterSearch]);

  const statsPlanning = React.useMemo(() => {
    const list = projectsAfterSearch.filter((p) => (p.status || '') === 'planning');
    return { count: list.length, sum: sumContractAmounts(list) };
  }, [projectsAfterSearch]);

  const filteredTotals = React.useMemo(
    () => ({
      count: filteredSorted.length,
      sum: sumContractAmounts(filteredSorted),
    }),
    [filteredSorted],
  );

  const countsByStatus = React.useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of projects) {
      const s = p.status || 'unknown';
      m[s] = (m[s] ?? 0) + 1;
    }
    return m;
  }, [projects]);

  const inProgressCount = countsByStatus.in_progress ?? 0;
  const hasFilters = Boolean(search.trim() || statusFilter);

  const columns = React.useMemo<ColumnDef<ProjectRow>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'پروژه',
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-slate-800">{row.original.label}</span>
            {row.original.status === 'in_progress' && (
              <span className="text-[11px] font-medium text-sky-600">اولویت نمایش — در حال اجرا</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'progress',
        header: 'پیشرفت',
        cell: ({ row }) => (
          <div className="flex min-w-[120px] items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${row.original.progress}%` }} />
            </div>
            <span className="w-10 text-left font-mono text-xs text-slate-600">{row.original.progress}%</span>
          </div>
        ),
      },
      {
        accessorKey: 'contractAmount',
        header: 'مبلغ قرارداد / برآورد',
        cell: ({ row }) => (
          <span className="font-mono text-sm text-slate-700">
            {row.original.contractAmount ? formatRial(row.original.contractAmount) : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'وضعیت',
        cell: ({ row }) => {
          const meta = statusMeta(row.original.status);
          return (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${meta.className}`}>
              {meta.icon}
              {meta.label}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button asChild variant="ghost" size="icon" title="مشاهده جزئیات پروژه">
            <Link to={`/projects/${row.original.id}`}>
              <ArrowLeftCircle className="h-5 w-5" />
            </Link>
          </Button>
        ),
      },
    ],
    [statusMeta],
  );

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="پروژه‌ها"
        description="پروژه‌های در حال اجرا بالای لیست قرار می‌گیرند؛ با فیلتر و جستجو سریع به هدف برس."
      />

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      ) : null}

      <LoadingLock loading={loading}>
      <Card className="border-indigo-200/70 bg-gradient-to-r from-indigo-50 via-sky-50 to-cyan-50">
        <CardContent className="flex flex-col gap-4 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <p className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
              <Sparkles className="h-3.5 w-3.5" />
              نمای اولویت‌دار
            </p>
            <h2 className="text-lg font-semibold text-slate-800">تمرکز روی پروژه‌های در جریان</h2>
            <p className="text-sm text-slate-600">
              الان {inProgressCount.toLocaleString('fa-IR')} پروژه در حال اجرا دارید؛ از کارت‌های زیر یا جدول برای ورود به
              جزئیات و هزینه‌ها استفاده کنید.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div className="rounded-xl border border-white/80 bg-white/90 px-3 py-2 shadow-sm">
              <p className="text-[11px] text-slate-500">کل پروژه</p>
              <p className="text-lg font-bold text-slate-800">{projects.length.toLocaleString('fa-IR')}</p>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 shadow-sm">
              <p className="text-[11px] text-sky-700">در حال اجرا</p>
              <p className="text-lg font-bold text-sky-800">{inProgressCount.toLocaleString('fa-IR')}</p>
            </div>
            <div className="col-span-2 rounded-xl border border-slate-200 bg-white/90 px-3 py-2 shadow-sm sm:col-span-1">
              <p className="text-[11px] text-slate-500">نتیجه فیلتر</p>
              <p className="text-lg font-bold text-slate-800">{rows.length.toLocaleString('fa-IR')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {inProgressForStrip.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-800">پروژه‌های در حال اجرا</h3>
            <span className="text-xs text-slate-500">{inProgressForStrip.length.toLocaleString('fa-IR')} مورد</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 pt-0.5 [scrollbar-width:thin]">
            {inProgressForStrip.map((p) => {
              const prog = Math.max(0, Math.min(100, Number(p.progress ?? 0)));
              const amt = contractAmountOf(p);
              return (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="min-w-[220px] max-w-[260px] shrink-0 rounded-xl border border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-indigo-50 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="inline-flex rounded-lg bg-sky-100 p-1.5 text-sky-700">
                      <FolderKanban className="h-4 w-4" />
                    </span>
                    <PlayCircle className="h-4 w-4 shrink-0 text-sky-600" />
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm font-semibold text-slate-800">{p.label}</p>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sky-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-indigo-500" style={{ width: `${prog}%` }} />
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">پیشرفت {prog.toLocaleString('fa-IR')}%</p>
                  {amt > 0 && <p className="mt-1 font-mono text-xs text-slate-600">{formatRial(amt)}</p>}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-cyan-50">
          <CardTitle className="text-base">{loading ? 'در حال بارگذاری...' : 'لیست پروژه‌ها'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-4 shadow-sm">
              <p className="text-xs font-medium text-sky-800">جمع مبلغ پیمان — در حال اجرا</p>
              <p className="mt-1 font-mono text-lg font-bold text-sky-900">{formatRial(statsInProgress.sum)}</p>
              <p className="mt-2 text-[11px] text-slate-600">
                {statsInProgress.count.toLocaleString('fa-IR')} پروژه
                {searchNorm ? ' · مطابق جستجو' : ''}
              </p>
            </div>
            <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
              <p className="text-xs font-medium text-violet-800">جمع مبلغ پیمان — در برنامه‌ریزی</p>
              <p className="mt-1 font-mono text-lg font-bold text-violet-900">{formatRial(statsPlanning.sum)}</p>
              <p className="mt-2 text-[11px] text-slate-600">
                {statsPlanning.count.toLocaleString('fa-IR')} پروژه
                {searchNorm ? ' · مطابق جستجو' : ''}
              </p>
            </div>
          </div>

          {(hasFilters || searchNorm) && (
            <div className="flex flex-col gap-1 rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2.5 text-sm text-indigo-950 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-medium">مطابق فیلتر فعلی (وضعیت + جستجو)</span>
              <span className="font-mono text-indigo-900">
                {filteredTotals.count.toLocaleString('fa-IR')} پروژه — {formatRial(filteredTotals.sum)}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 lg:grid-cols-12">
            <div className="relative min-w-0 lg:col-span-6">
              <label className="mb-1 block text-xs text-slate-600">جستجو در نام پروژه</label>
              <div className="relative">
                <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="مثلاً خط انتقال، ساختمان..."
                  className="pr-9"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="mb-1 block text-xs text-slate-600">وضعیت</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                    {o.value && countsByStatus[o.value] != null
                      ? ` (${(countsByStatus[o.value] ?? 0).toLocaleString('fa-IR')})`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end lg:col-span-3">
              <Button
                type="button"
                variant={hasFilters ? 'default' : 'outline'}
                className="w-full"
                disabled={!hasFilters}
                onClick={() => {
                  setSearch('');
                  setStatusFilter('');
                }}
              >
                پاک کردن فیلترها
              </Button>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs text-slate-600">فیلتر سریع (کلیک روی تگ)</p>
            <div className="flex flex-wrap gap-2">
              {FILTER_CHIPS.map((chip) => {
                const active = statusFilter === chip.key;
                const count =
                  chip.key === ''
                    ? projectsAfterSearch.length
                    : projectsAfterSearch.filter((p) => (p.status || '') === chip.key).length;
                return (
                  <button
                    key={chip.key || 'all'}
                    type="button"
                    onClick={() => setStatusFilter((prev) => (chip.key === '' ? '' : prev === chip.key ? '' : chip.key))}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm transition ${
                      active
                        ? 'border-indigo-500 bg-indigo-100 text-indigo-900 ring-1 ring-indigo-300'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/40'
                    }`}
                  >
                    {chip.label}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                        active ? 'bg-indigo-200/80 text-indigo-900' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {count.toLocaleString('fa-IR')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={hasFilters ? 'پروژه‌ای با این فیلتر پیدا نشد.' : 'پروژه‌ای ثبت نشده است.'}
          />
        </CardContent>
      </Card>
      </LoadingLock>
    </div>
  );
};

export default ProjectsPage;
